import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core'
import { Observable, Subject, Subscription, forkJoin, of } from 'rxjs'
import { takeUntil, finalize, retry, catchError, map } from 'rxjs/operators'
import { IDashboardService } from '../dashboard/dashboard.service'
import { ISalesFunnelApiResponse, TSalesFunnelCacheKey } from '../../models/dashboard.model'
import { UserStore } from '@core/services/user/user.store'
import { ESalesFunnelType } from '../../enum/sales-funnel.enum'
import { valueConstants } from '../../constants/valueConstants'
import { TViewType } from '../../models/ruc.model'
import { API_ERRORS } from '@app/core/constants/api-errors.constants'
import { ReporteesService } from '../reportees/reportees.service'
import type { ITeamAssignedSchoolScope } from '@app/features/crm/models/lead.model'

@Injectable({
    providedIn: 'root',
})
export class SalesFunnelService implements OnDestroy {
    private readonly dashboardService = inject(IDashboardService)
    private readonly userStore = inject(UserStore)
    private readonly reporteesService = inject(ReporteesService)
    private readonly destroy$ = new Subject<void>()
    private salesFunnelSubscription?: Subscription

    private readonly _cache = new Map<TSalesFunnelCacheKey, ISalesFunnelApiResponse | null>()
    private readonly _currentView = signal<TViewType>(valueConstants.USER_VIEW_QUERY_PARAM)
    private readonly _currentFunnelType = signal<ESalesFunnelType>(ESalesFunnelType.DEFAULT)
    private readonly _salesFunnelData = signal<ISalesFunnelApiResponse | null>(null)
    private readonly _isLoading = signal<boolean>(false)
    private readonly _error = signal<string | null>(null)

    readonly salesFunnelData = this._salesFunnelData.asReadonly()
    readonly isLoading = this._isLoading.asReadonly()
    readonly error = this._error.asReadonly()

    readonly stageMetrics = computed(() => {
        const data = this._salesFunnelData()
        return data?.data?.stage_wise_metrics || []
    })

    readonly keyMetrics = computed(() => {
        const data = this._salesFunnelData()
        return data?.data?.key_metrics || null
    })

    readonly newSchoolsWonCount = computed<number>(() => {
        const data = this._salesFunnelData()
        return data?.data?.key_metrics?.new_schools_won_count ?? 0
    })

    readonly leadStatusMetrics = computed(() => {
        const data = this._salesFunnelData()
        return data?.data?.lead_status_metrics || []
    })

    loadSalesFunnelData(
        funnelType: ESalesFunnelType = ESalesFunnelType.DEFAULT,
        isTeamView = false,
        forceRefresh = false,
    ): void {
        const view: TViewType = isTeamView
            ? valueConstants.TEAM_VIEW_QUERY_PARAM
            : valueConstants.USER_VIEW_QUERY_PARAM
        const cacheKey = this.buildCacheKey(funnelType, view)

        // Check cache first
        if (!forceRefresh) {
            const cachedData = this._cache.get(cacheKey)
            if (cachedData !== undefined) {
                this._currentView.set(view)
                this._currentFunnelType.set(funnelType)
                this._salesFunnelData.set(cachedData)
                this._error.set(null)
                return
            }
        }
        const userData = this.userStore.getUserData()
        if (!userData?.user_id) {
            this._error.set(API_ERRORS.USER_ID_NOT_AVAILABLE)
            return
        }

        this._isLoading.set(true)
        this._error.set(null)
        this.unsubscribe()

        const viewParam = isTeamView
            ? valueConstants.TEAM_VIEW_QUERY_PARAM
            : valueConstants.USER_VIEW_QUERY_PARAM

        this.salesFunnelSubscription = this.dashboardService
            .getSalesFunnelData(userData.user_id, funnelType, viewParam)
            .pipe(
                retry(1),
                takeUntil(this.destroy$),
                catchError(() => {
                    this._error.set(API_ERRORS.FETCH_FAILED)
                    return of(null)
                }),
                finalize(() => {
                    this._isLoading.set(false)
                }),
            )
            .subscribe({
                next: (data) => {
                    if (data) {
                        this._cache.set(cacheKey, data)
                        this._currentView.set(view)
                        this._currentFunnelType.set(funnelType)
                        this._salesFunnelData.set(data)
                    }
                },
            })
    }

    /**
     * Preload data for both user and team views
     */
    preloadAllViews(
        funnelType: ESalesFunnelType = ESalesFunnelType.DEFAULT,
        includeTeamView = false,
    ): void {
        const user = this.userStore.getUserData()
        if (!user?.user_id) return

        const views = includeTeamView
            ? [valueConstants.USER_VIEW_QUERY_PARAM, valueConstants.TEAM_VIEW_QUERY_PARAM]
            : [valueConstants.USER_VIEW_QUERY_PARAM]

        const requests$ = views.map((view) => {
            const cacheKey = this.buildCacheKey(funnelType, view)

            if (this._cache.has(cacheKey)) {
                return of({ view, data: null })
            }

            return this.dashboardService.getSalesFunnelData(user.user_id, funnelType, view).pipe(
                retry(1),
                catchError(() => of(null)),
                map((data) => ({ view, data })),
            )
        })

        this._isLoading.set(true)
        this._error.set(null)

        forkJoin(requests$)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this._isLoading.set(false)),
            )
            .subscribe((results) => {
                results.forEach(({ view, data }) => {
                    if (!data) return
                    const cacheKey = this.buildCacheKey(funnelType, view)
                    this._cache.set(cacheKey, data)
                })

                if (!this._salesFunnelData()) {
                    const currentView = this._currentView()
                    const cachedKey = this.buildCacheKey(funnelType, currentView)
                    const cachedData = this._cache.get(cachedKey)
                    if (cachedData) {
                        this._salesFunnelData.set(cachedData)
                    }
                }
            })
    }

    resolveTeamAssignedSchoolScopes(): Observable<ITeamAssignedSchoolScope[]> {
        const user = this.userStore.getUserData()
        if (!user?.user_id) {
            return of([])
        }

        return this.reporteesService.getReportees(user.user_id).pipe(
            map((response) => {
                const reporteeIds =
                    response.success && response.data?.reportees?.length
                        ? response.data.reportees
                              .map((reportee) => reportee.user_id)
                              .filter((userId) => !!userId?.trim())
                        : []
                const userIds = [...new Set([user.user_id, ...reporteeIds])]
                return userIds.map((userId) => ({ userId, clientIds: [] }))
            }),
            catchError(() => of([])),
        )
    }

    ngOnDestroy(): void {
        this.unsubscribe()
        this.destroy$.next()
        this.destroy$.complete()
    }

    private unsubscribe(): void {
        if (this.salesFunnelSubscription) {
            this.salesFunnelSubscription.unsubscribe()
            this.salesFunnelSubscription = undefined
        }
    }

    private buildCacheKey(funnelType: ESalesFunnelType, view: TViewType): TSalesFunnelCacheKey {
        if (funnelType !== ESalesFunnelType.DEFAULT) {
            return `${funnelType}_${view}` as TSalesFunnelCacheKey
        }

        const academicYearStart = this.dashboardService.selectedAcademicYearStart()
        if (!Number.isFinite(academicYearStart)) {
            return `${funnelType}_${view}` as TSalesFunnelCacheKey
        }

        return `${funnelType}_${view}_${academicYearStart}` as TSalesFunnelCacheKey
    }

    resolveTeamAssignedSchoolIds(): Observable<number[]> {
        return this.resolveTeamAssignedSchoolScopes().pipe(
            map((scopes) => scopes.flatMap((scope) => scope.clientIds)),
        )
    }
}
